import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert } from '../../components/Alert';
import { AuthCard } from '../../components/AuthCard';
import { ApiError } from '../../lib/api';
import { apiEspace } from '../../lib/espace';
import { dossiers, type Produit, type ProduitPropose } from '../../lib/dossiers';
import { useSessionPortail } from './useSessionPortail';
import { CreerEspace } from './CreerEspace';

/** Un espace où l'on peut créer un dossier (et donc payer) : propriétaire ou administrateur. */
interface EspaceGere {
  id: string;
  name: string;
  companies: { id: string; name: string; type?: unknown }[];
}
import '../../styles/dossiers.css';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Pourquoi un produit ne peut pas être coché (ou `null`). */
function raisonGrise(p: ProduitPropose, particulier: boolean): string | null {
  if (!p.lance) return 'Bientôt disponible';
  if (p.dejaOuvert) return 'Déjà ouvert pour ce dossier';
  if (!p.raccorde) return 'Momentanément indisponible';
  if (p.exigeSiret && particulier) return 'SEmplyApp nécessite un SIRET';
  return null;
}

/**
 * Étape 1 de la création : les produits à ouvrir (11/10/2026).
 *
 * Paramètres : `produit` (pré-coché), `dossier` (ouvrir un produit de plus sur
 * un dossier existant), `retour` (adresse du produit d'origine), `lie` (Previ :
 * dossier SEmply à lier), `espace` (29/10/2026 : l'espace qui portera le
 * nouveau dossier, et qu'on facturera). Un seul produit possible → étape sautée.
 */
export function NouveauDossierPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { pret } = useSessionPortail();
  const dossier = UUID.test(params.get('dossier') ?? '') ? params.get('dossier') : null;
  const retour = params.get('retour');
  const precoche = (params.get('produit') ?? '').toUpperCase() as Produit;

  const [produits, setProduits] = useState<ProduitPropose[] | null>(null);
  const [coches, setCoches] = useState<Produit[]>([]);
  const [espaces, setEspaces] = useState<EspaceGere[]>([]);
  const [espaceId, setEspaceId] = useState<string | null>(UUID.test(params.get('espace') ?? '') ? params.get('espace') : null);
  const [nouvelEspace, setNouvelEspace] = useState(false);
  const dossiersEspace = useMemo(
    () => (espaces.find((e) => e.id === espaceId) ?? espaces[0])?.companies ?? [],
    [espaces, espaceId],
  );
  const [lienPrevi, setLienPrevi] = useState<string>(UUID.test(params.get('lie') ?? '') ? params.get('lie')! : '');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  const particulier = useMemo(
    () => Boolean(dossier && dossiersEspace.find((d) => d.id === dossier)?.type === 'PARTICULIER'),
    [dossier, dossiersEspace],
  );

  useEffect(() => {
    if (!pret) return;
    void (async () => {
      try {
        const apercu = await apiEspace.apercu().catch(() => null);
        const geres: EspaceGere[] = (apercu?.espaces ?? [])
          .filter((e) => (e.role === 'OWNER' || e.role === 'ADMIN') && e.profil !== 'LECTURE_SEULE')
          .map((e) => ({ id: e.id, name: e.name, companies: e.companies as EspaceGere['companies'] }));
        setEspaces(geres);
        // L'espace demandé, s'il est l'un des miens ; sinon, le premier.
        const choisi = espaceId && geres.some((e) => e.id === espaceId) ? espaceId : (geres[0]?.id ?? null);
        // Espace corrigé : l'effet repart avec lui (et ne démarre pas deux créations).
        if (choisi !== espaceId) {
          setEspaceId(choisi);
          return;
        }
        const liste: ProduitPropose[] = await dossiers.produits(dossier, dossier ? null : choisi);
        setProduits(liste);
        const possibles = liste.filter((p) => !raisonGrise(p, false));
        if (possibles.some((p) => p.produit === precoche)) setCoches([precoche]);
        // Un seul produit possible : l'étape n'a pas lieu d'être.
        if (possibles.length === 1 && liste.filter((p) => p.lance).length === 1) {
          await commencer([possibles[0].produit]);
        }
      } catch (e) {
        setErreur(e instanceof ApiError ? e.message : 'Impossible de charger les produits.');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pret, dossier, espaceId]);

  async function commencer(choix: Produit[]) {
    setEnvoi(true);
    setErreur(null);
    try {
      const creation = await dossiers.creer({
        produits: choix,
        companyId: dossier,
        retourUrl: retour,
        lienPrevi: choix.includes('PREVI') && lienPrevi ? lienPrevi : null,
        spaceId: dossier ? null : espaceId,
      });
      navigate(`/dossiers/creation/${creation.id}`, { replace: true });
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Impossible de commencer la création.');
      setEnvoi(false);
    }
  }

  function basculer(p: Produit) {
    setCoches((c) => (c.includes(p) ? c.filter((x) => x !== p) : [...c, p]));
  }

  if (!pret || !produits) {
    return (
      <AuthCard title="Chargement…">
        <Alert message={erreur} />
      </AuthCard>
    );
  }

  return (
    <AuthCard
      className="dos-carte"
      title={dossier ? 'Ouvrir une application pour ce dossier' : 'Créer un dossier'}
      subtitle="Choisissez les applications à ouvrir. Un seul questionnaire servira à toutes."
    >
      <Alert message={erreur} />

      {!dossier && espaces.length > 0 ? (
        <div className="field">
          <label htmlFor="espace-dossier">Espace (la société facturée pour ce dossier)</label>
          {espaces.length > 1 ? (
            <select
              id="espace-dossier"
              value={espaceId ?? ''}
              disabled={envoi}
              onChange={(e) => {
                setLienPrevi('');
                setEspaceId(e.target.value);
              }}
            >
              {espaces.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
            </select>
          ) : (
            <p id="espace-dossier">
              <strong>{espaces[0].name}</strong>
            </p>
          )}
          {nouvelEspace ? (
            <CreerEspace
              onCree={(e) => {
                setNouvelEspace(false);
                setEspaces((liste) => [...liste, { id: e.id, name: e.name, companies: [] }]);
                setLienPrevi('');
                setEspaceId(e.id);
              }}
              onAnnuler={() => setNouvelEspace(false)}
            />
          ) : (
            <button type="button" className="btn-lien" onClick={() => setNouvelEspace(true)}>
              Facturer une autre société ? Créer un espace
            </button>
          )}
        </div>
      ) : null}

      <ul className="dos-produits">
        {produits.map((p) => {
          const raison = raisonGrise(p, particulier);
          const actif = coches.includes(p.produit);
          return (
            <li key={p.produit}>
              <label className={`dos-produit${actif ? ' is-actif' : ''}${raison ? ' is-grise' : ''}`}>
                <input type="checkbox" checked={actif} disabled={Boolean(raison) || envoi} onChange={() => basculer(p.produit)} />
                <span>
                  <strong>{p.nom}</strong>
                  {p.souscrit && p.formule ? (
                    <span className="dos-badge">{p.formule === 'free' ? 'Offre gratuite' : 'Abonnement en cours'}</span>
                  ) : null}
                  {p.description ? <small>{p.description}</small> : null}
                  {raison ? <small>{raison}</small> : null}
                  {!raison && p.quotaAtteint ? (
                    <small>Nombre de dossiers de votre formule atteint : vous pourrez la faire évoluer à l’étape suivante.</small>
                  ) : null}
                </span>
              </label>
            </li>
          );
        })}
      </ul>

      {coches.includes('PREVI') && !dossier && dossiersEspace.length > 0 ? (
        <div className="field">
          <label htmlFor="lien-previ">Lier le prévisionnel à un dossier SEmply existant (facultatif)</label>
          <select id="lien-previ" value={lienPrevi} onChange={(e) => setLienPrevi(e.target.value)}>
            <option value="">— Aucun —</option>
            {dossiersEspace.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      <button className="btn" type="button" disabled={coches.length === 0 || envoi} onClick={() => void commencer(coches)}>
        {envoi ? 'Un instant…' : 'Continuer'}
      </button>
    </AuthCard>
  );
}
